import { Injectable, OnDestroy } from "@angular/core";
import { FormControl, FormGroup, UntypedFormBuilder } from "@angular/forms";
import { Store } from "@ngrx/store";
import { CommonState } from "@obg/ngx.common";
import { Jurisdiction, MultiDepositLimit, MultiDepositLimitAction, MultiDepositLimitResponse } from "@obg/ngx.core";
import { merge, Subscription } from "rxjs";
import { tap } from "rxjs/operators";

import { OBG_VALIDATORS } from "../../../validators/validators";
import {
	currentValuesEqualsFormControlValuesValidator,
	validateForm,
} from "../../responsible-gaming-form-validators/validate-form";

import { MultiDepositFormValidatonService } from "./multi-deposit-form-validaton.service";
import { MultiDepositLimitConfig } from "./multi-deposit-limit.config";
import { MultiDepositLimitType } from "./multi-deposit-limit.const";
import { MultiDepositLimitFormGroup, MultiDepositLimitFormSuggestions } from "./multi-deposit-limit.model";

@Injectable()
export class MultiDepositLimitService implements OnDestroy {
	multiDepositLimitFormGroup: MultiDepositLimitFormGroup;

	currentLimits: MultiDepositLimitResponse | undefined;
	upcomingLimits: MultiDepositLimitResponse | undefined;

	suggestions: MultiDepositLimitFormSuggestions = {
		weeklyDepositLimit: undefined,
		monthlyDepositLimit: undefined,
	};

	private data$$: Subscription | undefined;

	constructor(
		private formBuilder: UntypedFormBuilder,
		private store: Store<CommonState>,
		private multiDepositLimitAction: MultiDepositLimitAction,
		private multiDepositLimitConfig: MultiDepositLimitConfig,
		private multiDepositFormValidatonService: MultiDepositFormValidatonService
	) {
		this.multiDepositLimitFormGroup = this.initStandardFormControls();
	}

	init(
		currentLimits: MultiDepositLimitResponse | undefined,
		upcomingLimits: MultiDepositLimitResponse | undefined,
		jurisdiction: Jurisdiction | undefined
	) {
		this.currentLimits = currentLimits;
		this.upcomingLimits = upcomingLimits;

		if (this.multiDepositLimitConfig.isDefaultValuesEnabled()) {
			this.setDefaultValues(this.currentLimits);
		}

		const formControlChanges$ = this.multiDepositLimitFormGroup.valueChanges.pipe(
			tap((depositLimits) => {
				if (jurisdiction === Jurisdiction.Sga) {
					this.updatePlaceholders(depositLimits);
				}

				if (jurisdiction === Jurisdiction.COGA) {
					this.setStandardValidators();
					this.setCurrentValuesEqualFormControlValuesValidator(depositLimits);
					this.updateFormGroupValidators(this.multiDepositLimitFormGroup);

					return;
				}

				this.setStandardValidators();
				this.setCurrentAndUpcomingDepositLimitsValidators(depositLimits);
				this.updateFormGroupValidators(this.multiDepositLimitFormGroup);
			})
		);

		this.data$$ = merge(formControlChanges$).subscribe();
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
	}

	updateValue() {
		this.store.dispatch(this.multiDepositLimitAction.update(this.multiDepositLimitFormGroup.value));
	}

	reset() {
		this.multiDepositLimitFormGroup.reset();
	}

	private initStandardFormControls(): MultiDepositLimitFormGroup {
		const dailyDepositLimit = new FormControl();
		const weeklyDepositLimit = new FormControl();
		const monthlyDepositLimit = new FormControl();
		const monthlyDepositLimitMaxValue = this.multiDepositLimitConfig.getMonthlyDepositLimitMaxValue();

		dailyDepositLimit.setValidators(this.multiDepositFormValidatonService.getDailyValidators(monthlyDepositLimit));
		weeklyDepositLimit.setValidators(
			this.multiDepositFormValidatonService.getWeeklyValidators(monthlyDepositLimit)
		);

		const monthlyValidators = this.multiDepositFormValidatonService.getBaseValidators(
			true,
			monthlyDepositLimitMaxValue ? monthlyDepositLimitMaxValue : undefined,
			undefined
		);
		monthlyDepositLimit.setValidators([
			...monthlyValidators,
			OBG_VALIDATORS.numberBiggerOrEqual(() => dailyDepositLimit.value),
			OBG_VALIDATORS.numberBiggerOrEqual(() => weeklyDepositLimit.value),
		]);

		return this.formBuilder.group({
			dailyDepositLimit,
			weeklyDepositLimit,
			monthlyDepositLimit,
		}) as MultiDepositLimitFormGroup;
	}

	private updateFormGroupValidators(formGroup: FormGroup) {
		Object.keys(formGroup.controls).forEach((key) => {
			const control = formGroup.controls[key];
			control.updateValueAndValidity({ onlySelf: true });
		});
		formGroup.updateValueAndValidity({ emitEvent: false });
	}

	private updatePlaceholders(limits: MultiDepositLimit) {
		const dailyDepositLimit = Number(limits.dailyDepositLimit);
		const weeklyDepositLimit = Number(limits.weeklyDepositLimit);
		const monthlyDepositLimit = Number(limits.monthlyDepositLimit);

		if (
			!this.multiDepositFormValidatonService.isValueValid(dailyDepositLimit) ||
			!this.multiDepositFormValidatonService.isValueValid(weeklyDepositLimit) ||
			!this.multiDepositFormValidatonService.isValueValid(monthlyDepositLimit)
		) {
			this.setDefaultPlaceholders();
			return;
		}

		if (dailyDepositLimit && weeklyDepositLimit && dailyDepositLimit * 7 < weeklyDepositLimit) {
			this.suggestions.monthlyDepositLimit = (dailyDepositLimit * 30).toString();
			return;
		}

		if (dailyDepositLimit) {
			this.suggestions.weeklyDepositLimit = (dailyDepositLimit * 7).toString();
			this.suggestions.monthlyDepositLimit = (dailyDepositLimit * 30).toString();
			return;
		}

		this.setDefaultPlaceholders();
	}

	private setCurrentAndUpcomingDepositLimitsValidators(depositLimits: MultiDepositLimit) {
		this.multiDepositFormValidatonService.forceInvalidForm = false;

		const dailyDepositLimit = depositLimits.dailyDepositLimit ? Number(depositLimits.dailyDepositLimit) : undefined;
		const weeklyDepositLimit = depositLimits.weeklyDepositLimit
			? Number(depositLimits.weeklyDepositLimit)
			: undefined;
		const monthlyDepositLimit = Number(depositLimits.monthlyDepositLimit);

		if (this.currentLimits && !this.upcomingLimits) {
			const { validationKey, forceInvalidForm } = validateForm(
				[
					this.currentLimits.dailyDepositLimit,
					this.currentLimits.weeklyDepositLimit,
					this.currentLimits.monthlyDepositLimit,
				],
				[dailyDepositLimit, weeklyDepositLimit, monthlyDepositLimit],
				MultiDepositLimitType.Current
			);
			this.multiDepositFormValidatonService.forceInvalidForm = forceInvalidForm;
			this.multiDepositFormValidatonService.validationKey = validationKey;
		}

		if (
			!this.multiDepositFormValidatonService.forceInvalidForm &&
			this.upcomingLimits &&
			this.upcomingLimits.dailyDepositLimit !== 0
		) {
			const { validationKey, forceInvalidForm } = validateForm(
				[
					this.upcomingLimits.dailyDepositLimit,
					this.upcomingLimits.weeklyDepositLimit,
					this.upcomingLimits.monthlyDepositLimit,
				],
				[dailyDepositLimit, weeklyDepositLimit, monthlyDepositLimit],
				MultiDepositLimitType.Upcoming
			);
			this.multiDepositFormValidatonService.forceInvalidForm = forceInvalidForm;
			this.multiDepositFormValidatonService.validationKey = validationKey;
		}
		return;
	}

	private setCurrentValuesEqualFormControlValuesValidator(depositLimits: MultiDepositLimit) {
		this.multiDepositFormValidatonService.forceInvalidForm = false;

		const dailyDepositLimit = depositLimits.dailyDepositLimit ? Number(depositLimits.dailyDepositLimit) : undefined;
		const weeklyDepositLimit = depositLimits.weeklyDepositLimit
			? Number(depositLimits.weeklyDepositLimit)
			: undefined;
		const monthlyDepositLimit = Number(depositLimits.monthlyDepositLimit);

		const { validationKey, forceInvalidForm } = currentValuesEqualsFormControlValuesValidator(
			[
				this.currentLimits?.dailyDepositLimit,
				this.currentLimits?.weeklyDepositLimit,
				this.currentLimits?.monthlyDepositLimit,
			],
			[dailyDepositLimit, weeklyDepositLimit, monthlyDepositLimit],
			MultiDepositLimitType.Current
		);
		this.multiDepositFormValidatonService.forceInvalidForm = forceInvalidForm;
		this.multiDepositFormValidatonService.validationKey = validationKey;
	}

	private setDefaultValues(currentLimits: MultiDepositLimitResponse | undefined): void {
		this.multiDepositLimitFormGroup.patchValue({
			dailyDepositLimit: currentLimits?.dailyDepositLimit,
			weeklyDepositLimit: currentLimits?.weeklyDepositLimit,
			monthlyDepositLimit: currentLimits?.monthlyDepositLimit,
		});
	}

	private setDefaultPlaceholders() {
		this.suggestions = {
			weeklyDepositLimit: undefined,
			monthlyDepositLimit: undefined,
		};
	}

	private setStandardValidators() {
		const daily = this.multiDepositLimitFormGroup.controls.dailyDepositLimit;
		const weekly = this.multiDepositLimitFormGroup.controls.weeklyDepositLimit;
		const monthly = this.multiDepositLimitFormGroup.controls.monthlyDepositLimit;

		const dailyValidators = this.multiDepositFormValidatonService.getDailyValidators(monthly);
		const weeklyValidators = this.multiDepositFormValidatonService.getWeeklyValidators(monthly);
		if (daily?.value && weekly?.value) {
			dailyValidators.push(OBG_VALIDATORS.numberSmallerOrEqual(() => weekly.value));
			weeklyValidators.push(OBG_VALIDATORS.numberBiggerOrEqual(() => daily.value));
		}

		this.multiDepositLimitFormGroup.controls.dailyDepositLimit?.setValidators(dailyValidators);
		this.multiDepositLimitFormGroup.controls.weeklyDepositLimit?.setValidators(weeklyValidators);
	}
}
